import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import ClientsHeader from '@/components/ClientsHeader';
import ClientsSearch from '@/components/ClientsSearch';
import ClientsTable from '@/components/ClientsTable';
import { ClientDeleteDialog } from '@/components/ClientDeleteDialog';
import { ClientForm } from '@/components/ClientForm';
import BusinessRulesModal from '@/components/BusinessRulesModal';
import ProductsModal from '@/components/ProductsModal';
import ClientFinancialModal from '@/components/ClientFinancialModal';
import PasswordConfirmationModal from '@/components/modals/PasswordConfirmationModal';
import EmitirContratoModal from '@/components/modals/EmitirContratoModal';
import { Client } from '@/types/client';
import { apiService } from '@/lib/api';
import RoleBasedNavigation from '@/components/RoleBasedNavigation';
import { toast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

function EditAsaasIdDialog({
  value,
  onChange,
  onSave,
  onClose,
  saving,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar ID Asaas (Customer ID)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-asaas-id">Novo ID Asaas</Label>
            <Input
              id="edit-asaas-id"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="cus_000000000000"
              className="font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving || !value.trim()}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Clients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  
  const handleSearchChange = (value: string) => {
    console.log('🔍 [Clients] handleSearchChange chamado com:', value);
    setSearchTerm(value);
  };
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBusinessRulesOpen, setIsBusinessRulesOpen] = useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedClientForProducts, setSelectedClientForProducts] = useState<Client | null>(null);
  const [selectedClientForFinancial, setSelectedClientForFinancial] = useState<Client | null>(null);
  const [showPasswordModalForAsaas, setShowPasswordModalForAsaas] = useState(false);
  const [showEditAsaasDialog, setShowEditAsaasDialog] = useState(false);
  const [pendingPasswordForAsaas, setPendingPasswordForAsaas] = useState<string>('');
  const [editAsaasValue, setEditAsaasValue] = useState('');
  const [savingAsaasEdit, setSavingAsaasEdit] = useState(false);
  const [contractModalClient, setContractModalClient] = useState<Client | null>(null);

  useEffect(() => {
    console.log('ClientsPage: Componente montado. Iniciando fetch.');
    fetchClients();
  }, []);

  const fetchClients = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      console.log('ClientsPage: Fazendo requisição para API de clientes...');
      const data = await apiService.getClients();
      console.log('ClientsPage: Dados recebidos da API:', data);
      console.log('ClientsPage: Número de clientes:', data.length);
      setClients(data);
    } catch (err) {
      console.error('ClientsPage: Erro ao carregar clientes:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao carregar clientes';
      setError(`Erro ao carregar clientes: ${errorMessage}`);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      console.log('ClientsPage: Fetch de clientes finalizado.');
    }
  };

  const filteredClients = (Array.isArray(clients) ? clients : []).filter(client => {
    // Filtro de texto
    const matchesSearch = client.legal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company_tax_id.includes(searchTerm) ||
      (client.trade_name && client.trade_name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filtro de status
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && client.is_active) ||
      (statusFilter === 'inactive' && !client.is_active);

    // Filtro de data
    let matchesDate = true;
    if (dateFromFilter || dateToFilter) {
      const clientDate = new Date(client.created_at);
      if (dateFromFilter) {
        const fromDate = new Date(dateFromFilter);
        matchesDate = matchesDate && clientDate >= fromDate;
      }
      if (dateToFilter) {
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999); // Include the entire day
        matchesDate = matchesDate && clientDate <= toDate;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleNewClient = () => {
    setSelectedClient(null);
    setIsFormOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsFormOpen(true);
  };

  const handleDeleteClient = (client: Client) => {
    setSelectedClient(client);
    setIsDeleteDialogOpen(true);
  };

  const handleBusinessRules = (client: Client) => {
    console.log('🎯 [Clients] handleBusinessRules chamado para cliente:', {
      id: client.id,
      legal_name: client.legal_name,
      trade_name: client.trade_name
    });
    setSelectedClient(client);
    setIsBusinessRulesOpen(true);
  };

  const handleProductsClick = (client: Client) => {
    setSelectedClientForProducts(client);
    setIsProductsModalOpen(true);
  };

  const handleContractClick = (client: Client) => {
    setContractModalClient(client);
  };

  const handleFinancialClick = (client: Client) => {
    if (!client.asaas_id || String(client.asaas_id).trim() === '') {
      toast({
        title: 'ID Asaas não cadastrado',
        description: 'Cadastre o ID Asaas do cliente nos dados do cliente para visualizar as cobranças.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedClientForFinancial(client);
    setIsFinancialModalOpen(true);
  };

  const handleSaveClient = async (clientData: Partial<Client>) => {
    try {
      console.log('ClientsPage: Salvando cliente:', clientData);
      console.log('ClientsPage: Cliente selecionado:', selectedClient);

      let savedClient;
      if (selectedClient) {
        console.log('ClientsPage: Editando cliente ID:', selectedClient.id);
        savedClient = await apiService.updateClient(selectedClient.id, clientData);
      } else {
        console.log('ClientsPage: Criando novo cliente');
        savedClient = await apiService.createClient(clientData);
      }

      console.log('ClientsPage: Cliente salvo com sucesso:', savedClient);
      setIsFormOpen(false);
      setSelectedClient(null);
      setTimeout(() => fetchClients(false), 100);
    } catch (err) {
      console.error('ClientsPage: Erro fatal ao salvar cliente:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao salvar cliente';
      setError(`Erro ao atualizar cliente: ${errorMessage}`);
    }
  };

  const handleRequestEditAsaasId = () => {
    setEditAsaasValue(selectedClient?.asaas_id ?? '');
    setShowPasswordModalForAsaas(true);
  };

  const handleConfirmPasswordForAsaasEdit = async (password: string) => {
    setPendingPasswordForAsaas(password);
    setShowPasswordModalForAsaas(false);
    setShowEditAsaasDialog(true);
  };

  const handleSaveAsaasEdit = async () => {
    if (!selectedClient) return;
    setSavingAsaasEdit(true);
    try {
      const updated = await apiService.updateClient(selectedClient.id, {
        asaas_id: editAsaasValue.trim(),
        password: pendingPasswordForAsaas,
      });
      setShowEditAsaasDialog(false);
      setPendingPasswordForAsaas('');
      setEditAsaasValue('');
      setSelectedClient(updated as Client);
      toast({ title: 'ID Asaas atualizado', description: 'O ID Asaas do cliente foi alterado.' });
      setTimeout(() => fetchClients(false), 100);
    } catch (err: any) {
      const msg = err?.message ?? err?.error ?? err?.response?.data?.error ?? 'Erro ao atualizar ID Asaas.';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSavingAsaasEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (selectedClient) {
      try {
        console.log('ClientsPage: Excluindo cliente ID:', selectedClient.id);
        
        await apiService.deleteClient(selectedClient.id);

        console.log('ClientsPage: Cliente excluído com sucesso');
        
        // Fechar modal e atualizar lista
        setIsDeleteDialogOpen(false);
        setSelectedClient(null);
        fetchClients(false); // Não mostrar loading ao atualizar após exclusão
      } catch (err) {
        console.error('ClientsPage: Erro ao excluir cliente:', err);
        setError(err instanceof Error ? err.message : 'Erro ao excluir cliente');
      }
    }
  };

  if (loading) {
    return (
      <RoleBasedNavigation>
        <Layout>
          <AtomicLoadingScreen 
            message="Carregando clientes..." 
            className="h-64"
          />
        </Layout>
      </RoleBasedNavigation>
    );
  }

  if (error) {
    return (
      <RoleBasedNavigation>
        <Layout><div className="text-red-500">Erro ao carregar clientes: {error}</div></Layout>
      </RoleBasedNavigation>
    );
  }

  return (
    <RoleBasedNavigation>
      <Layout>
      <div className="space-y-6">
        <ClientsHeader onNewClient={handleNewClient} />
        <ClientsSearch 
          searchTerm={searchTerm} 
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          dateFromFilter={dateFromFilter}
          onDateFromFilterChange={setDateFromFilter}
          dateToFilter={dateToFilter}
          onDateToFilterChange={setDateToFilter}
        />
        <ClientsTable 
          clients={filteredClients}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onBusinessRules={handleBusinessRules}
          onProductsClick={handleProductsClick}
          onFinancialClick={handleFinancialClick}
          onContractClick={handleContractClick}
        />
      </div>

      {/* Modal de Exclusão */}
      {isDeleteDialogOpen && selectedClient && (
        <ClientDeleteDialog
          client={selectedClient}
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSelectedClient(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {/* Modal de Cadastro/Edição */}
      <ClientForm
        client={selectedClient}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedClient(null);
        }}
        onSave={handleSaveClient}
        onRequestEditAsaasId={selectedClient ? handleRequestEditAsaasId : undefined}
      />

      {/* Modal de Regras de Negócio */}
      <BusinessRulesModal
        client={selectedClient}
        isOpen={isBusinessRulesOpen}
        onClose={() => {
          setIsBusinessRulesOpen(false);
          setSelectedClient(null);
        }}
      />

      {/* Modal Produtos e observações */}
      <ProductsModal
        client={selectedClientForProducts}
        isOpen={isProductsModalOpen}
        onClose={() => {
          setIsProductsModalOpen(false);
          setSelectedClientForProducts(null);
        }}
      />

      {/* Modal Financeiro (Cobranças Asaas) */}
      <ClientFinancialModal
        client={selectedClientForFinancial}
        isOpen={isFinancialModalOpen}
        onClose={() => {
          setIsFinancialModalOpen(false);
          setSelectedClientForFinancial(null);
        }}
      />

      {/* Modal de confirmação de senha para editar asaas_id */}
      <PasswordConfirmationModal
        isOpen={showPasswordModalForAsaas}
        onClose={() => setShowPasswordModalForAsaas(false)}
        onConfirm={handleConfirmPasswordForAsaasEdit}
        title="Confirmar senha"
        description="Para alterar o ID Asaas é necessário informar sua senha de usuário."
      />

      {/* Modal Emitir Contrato */}
      <EmitirContratoModal
        isOpen={!!contractModalClient}
        onClose={() => setContractModalClient(null)}
        client={contractModalClient}
        onSuccess={() => {
          toast({ title: 'Contrato gerado', description: 'O PDF foi baixado com sucesso.' });
        }}
      />

      {/* Dialog para editar ID Asaas (após confirmar senha) */}
      {showEditAsaasDialog && (
        <EditAsaasIdDialog
          value={editAsaasValue}
          onChange={setEditAsaasValue}
          onSave={handleSaveAsaasEdit}
          onClose={() => {
            setShowEditAsaasDialog(false);
            setPendingPasswordForAsaas('');
          }}
          saving={savingAsaasEdit}
        />
      )}
    </Layout>
    </RoleBasedNavigation>
  );
};

export default Clients;
